// Browser-side identity primitives — keypair generation, WIF encoding,
// and IndexedDB persistence. Mirrors the server-side contract in
// `billing-service/src/auth-signature.ts`: account_id is base58 of the
// SEC1-compressed pubkey; the privkey is exported as standard WIF
// (base58check, version 0x80 for mainnet, 0x01 compressed-flag suffix)
// so any BCH wallet can import the customer's recovery secret.

import {
  binToBase58,
  decodePrivateKeyWif,
  encodePrivateKeyWif,
  hexToBin,
  secp256k1,
} from '@bitauth/libauth';

export interface Identity {
  privkey:    Uint8Array;   // 32 bytes
  account_id: string;       // base58(33-byte compressed pubkey)
  wif:        string;       // 52-char WIF for backup display
}

export const DB_NAME    = 'solidpeer';
// Bumped to 2 to add the `raw_tokens` store. onupgradeneeded creates
// any store that doesn't already exist, so the bump is compatible
// with old browsers that already have v1.
export const DB_VERSION = 2;
const STORE      = 'identity';
const KEY        = 'self';

// ============================================================
// Keypair construction / import
// ============================================================

/**
 * Generate a fresh secp256k1 keypair. Calls into WebCrypto for the
 * 32 bytes of entropy, then derives the SEC1-compressed pubkey via
 * libauth.
 */
export function generateIdentity(): Identity {
  const privkey = new Uint8Array(32);
  crypto.getRandomValues(privkey);
  return identityFromPrivkey(privkey);
}

/**
 * Reconstruct an Identity from a WIF string. Throws on malformed WIF
 * (bad checksum, wrong length, etc.) or on the rare uncompressed
 * variants — the dashboard only handles compressed keypairs.
 */
export function identityFromWif(wif: string): Identity {
  const decoded = decodePrivateKeyWif(wif);
  if (typeof decoded === 'string') {
    throw new Error(`invalid WIF: ${decoded}`);
  }
  if (decoded.type === 'mainnetUncompressed' || decoded.type === 'testnetUncompressed') {
    throw new Error('uncompressed-pubkey WIF not supported — generate a compressed key');
  }
  if (decoded.privateKey.length !== 32) {
    throw new Error(`WIF private key must be 32 bytes (got ${decoded.privateKey.length})`);
  }
  return identityFromPrivkey(decoded.privateKey);
}

function identityFromPrivkey(privkey: Uint8Array): Identity {
  const pub = secp256k1.derivePublicKeyCompressed(privkey);
  if (typeof pub === 'string') throw new Error(`derive pub failed: ${pub}`);
  const account_id = binToBase58(pub);
  const wif        = encodePrivateKeyWif(privkey, 'mainnet');
  return { privkey, account_id, wif };
}

/** Display helper — first 12 chars of the base58 account_id. */
export function shortAccountId(account_id: string): string {
  return account_id.length <= 14 ? account_id : `${account_id.slice(0, 12)}…`;
}

// ============================================================
// IndexedDB persistence
// ============================================================
//
// Stored shape: { privkey: Uint8Array, account_id: string }.
// WIF is recomputed on load; not stored (one source of truth).

interface StoredRecord {
  privkey:    Uint8Array;
  account_id: string;
}

/** Shared opener — also used by token-cache.ts. The onupgradeneeded
 *  block creates any missing store, so adding stores in future
 *  versions is just a matter of bumping DB_VERSION and listing them
 *  here. */
export async function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE))      db.createObjectStore(STORE);
      if (!db.objectStoreNames.contains('raw_tokens')) db.createObjectStore('raw_tokens');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error ?? new Error('indexedDB open failed'));
  });
}

export async function saveIdentity(id: Identity): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const record: StoredRecord = { privkey: id.privkey, account_id: id.account_id };
    tx.objectStore(STORE).put(record, KEY);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error ?? new Error('indexedDB put failed'));
  });
  db.close();
}

export async function loadIdentity(): Promise<Identity | null> {
  const db = await openDb();
  const record = await new Promise<StoredRecord | undefined>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(KEY);
    req.onsuccess = () => resolve(req.result as StoredRecord | undefined);
    req.onerror   = () => reject(req.error ?? new Error('indexedDB get failed'));
  });
  db.close();
  if (!record) return null;
  // Re-derive pubkey + WIF from the stored privkey rather than trusting
  // the persisted account_id — defends against a tampered IndexedDB
  // value where account_id and privkey diverge.
  return identityFromPrivkey(record.privkey);
}

export async function clearIdentity(): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    // Wipe both stores in a single tx: signout forgets the privkey
    // and the cached raw tokens together. The token cache exists
    // only for this device's UX (re-rendering issued tokens in
    // endpoint URLs); without the privkey it's useless.
    const tx = db.transaction([STORE, 'raw_tokens'], 'readwrite');
    tx.objectStore(STORE).delete(KEY);
    tx.objectStore('raw_tokens').clear();
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error ?? new Error('indexedDB delete failed'));
  });
  db.close();
}

// Re-export hexToBin for any caller that needs to round-trip an
// operator-style hex privkey through identityFromPrivkey (debug paths).
export { hexToBin };
